<template>
    <div class="cf">
        <div class="cf__row">
            <UserIcon />
            <div class="cf__body">
                <!-- Collapsed trigger -->
                <div
                    v-if="!showCommentInput"
                    class="cf__trigger"
                    @click="showCommentInput = true"
                >
                    <i class="far fa-comment"></i>
                    <span>Add a comment...</span>
                </div>

                <!-- Expanded editor -->
                <div v-if="showCommentInput" class="cf__editor">
                    <HtmlEditor
                        :id="`comment-input`"
                        @change="comment = $event"
                        :value="comment"
                    />
                    <TagUser
                        :hideSave="true"
                        :createRoute="'/project-comments'"
                        :updateRoute="commentId ? '/project-comments/' + commentId : null"
                        :taggedUsers="taggedUsers || []"
                        @change="handleTaggedUsersChange"
                    />
                    <div class="cf__attachments" v-if="showAttachmentManager">
                        <AttachmentUploader
                            @remove="addToRemoveList('attachment1')"
                            v-model="attachment1"
                            :id="1"
                            :name="attachment1Name"
                        />
                        <AttachmentUploader
                            @remove="addToRemoveList('attachment2')"
                            v-model="attachment2"
                            :id="2"
                            :name="attachment2Name"
                        />
                        <AttachmentUploader
                            @remove="addToRemoveList('attachment3')"
                            v-model="attachment3"
                            :id="3"
                            :name="attachment3Name"
                        />
                    </div>
                    <div class="cf__actions">
                        <button
                            class="cf__btn cf__btn--primary"
                            @click="handleCommentSave"
                            :disabled="processingForm"
                        >
                            <i class="fas fa-spinner fa-spin" v-if="processingForm"></i>
                            <i class="fas fa-paper-plane" v-else></i>
                            {{ processingForm ? "Saving..." : "Save" }}
                        </button>
                        <button class="cf__btn cf__btn--ghost" @click="cancelEditing">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import HtmlEditor from "@/components/other/HtmlEditor"
import Button from "@/components/UI/Button"
import AttachmentUploader from "@/components/other/AttachmentUploader"
import TagUser from "@/components/other/TagUser.vue"
import UserIcon from "./UserIcon.vue"
import { http } from "@/utils/http"

export default {
    data() {
        return {
            commentId: "",
            showCommentInput: false,
            comment: "",
            comments: [],
            loading: false,
            processingForm: false,
            removeFilesList: [],
            attachment1: null,
            attachment2: null,
            attachment3: null,
            attachment1Name: "",
            attachment2Name: "",
            attachment3Name: "",
            showAttachmentManager: true,
            taggedUsers: [],
        }
    },
    props: {
        taskId: { type: [String, Number], default: "" },
        commentData: { type: Object, default: () => null },
    },
    components: { HtmlEditor, Button, AttachmentUploader, UserIcon, TagUser },
    mounted() {
        if (this.commentData) {
            this.showCommentInput = true
            this.commentId = this.commentData._id
            this.comment = this.commentData.description || ""
            this.attachment1Name = this.commentData.attachment1
            this.attachment2Name = this.commentData.attachment2
            this.attachment3Name = this.commentData.attachment3
            this.taggedUsers = this.commentData.taggedUsers
        }
    },
    methods: {
        cancelEditing() {
            this.showCommentInput = false
            this.$emit("cancel")
        },
        handleTaggedUsersChange(update) {
            this.taggedUsers = update
        },
        async handleCommentSave() {
            if (this.commentData) {
                this.updateComment()
            } else {
                this.postComment()
            }
        },
        async updateComment() {
            this.postComment(true)
        },
        async postComment(updateMode = true) {
            if (this.processingForm) return
            this.processingForm = true
            try {
                const formData = new FormData()

                formData.append("description", this.comment || "")
                formData.append("taskId", this.taskId)

                // files to remove if any

                // attachments to add if any
                formData.append("attachment1", this.attachment1)
                formData.append("attachment2", this.attachment2)
                formData.append("attachment3", this.attachment3)
                formData.append("taggedUsers", JSON.stringify(this.taggedUsers))

                if (updateMode && this.commentData) {
                    formData.append("removeFilesList", JSON.stringify(this.removeFilesList))
                    const res = await http.patch(
                        "/project-comments/" + this.commentData._id,
                        formData
                    )
                    this.$emit("updated", res.data.result)
                } else {
                    const res = await http.post("/project-comments", formData)
                    this.$emit("inserted", res.data.result)
                }
                this.showCommentInput = false
                this.comment = ""
            } catch (err) {
                console.log("error in fetching comment", err)
            }
            this.processingForm = false
        },
        async addToRemoveList(item) {
            if (!this.removeFilesList.includes(item)) {
                this.removeFilesList = [...this.removeFilesList, item]
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.cf {
    &__row {
        display: flex;
        gap: 10px;
        align-items: flex-start;
    }
    &__body {
        flex: 1;
        min-width: 0;
    }
    &__trigger {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 14px;
        border: 1px solid $gray200;
        border-radius: $radiusSm;
        color: $gray400;
        font-size: 13px;
        cursor: pointer;
        transition: all $transitionFast;
        background: white;

        i { font-size: 13px; }

        &:hover {
            border-color: $gray300;
            color: $gray500;
            background: $gray50;
        }
    }
    &__editor {
        border: 1px solid $gray200;
        border-radius: $radiusSm;
        overflow: hidden;
        background: white;
    }
    &__attachments {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
        padding: 10px 14px;
        border-top: 1px solid $gray100;
    }
    &__actions {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 14px;
        border-top: 1px solid $gray100;
        background: $gray50;
    }
    &__btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 7px 16px;
        border-radius: $radiusSm;
        font-size: 12px;
        font-weight: 600;
        font-family: inherit;
        cursor: pointer;
        border: none;
        transition: all $transitionFast;

        &:disabled { opacity: 0.55; cursor: not-allowed; }

        &--primary {
            background: $primaryColor;
            color: white;
            &:hover:not(:disabled) { background: $primaryDark; }
        }
        &--ghost {
            background: white;
            color: $gray600;
            border: 1px solid $gray200;
            &:hover { background: $gray50; color: $gray800; }
        }
    }
}
</style>
